import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import s3Service from "../services/s3Service";
import ticketsService from "../services/ticketsService";
import { useAuth } from "../contexts/AuthContext";
import DateInput from "./DateInput";
import { isValidDateUK, addMonthsToDate } from "../utils/dateUtils";
import {
  CERTIFICATE_TYPES,
  certificateTypeFromServiceName,
  validityMonthsForType,
  validateCertificateFile,
  CERTIFICATE_ACCEPT,
  CERTIFICATE_FILE_TYPES,
  CERTIFICATE_MAX_SIZE,
} from "../utils/certificateTypes";
import styles from "./TicketCertificatePanel.module.css";

// DD/MM/YYYY -> YYYY-MM-DD without touching Date (avoids timezone shifting).
const toISODateSafe = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length !== 3) return dateStr;
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// DD/MM/YYYY + months -> DD/MM/YYYY (built from parts so it stays local-time).
const addMonthsUK = (ukDate, months) => {
  const parts = (ukDate || "").split("/");
  if (parts.length !== 3) return "";
  const base = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
  if (isNaN(base.getTime())) return "";
  return addMonthsToDate(base, months);
};

/**
 * Contractor "Upload certificate & mark complete" panel.
 * Renders only on a recognised certificate ticket for a non-guardian/non-client user.
 * Submits the certificate + moves the ticket to Contractor Completed (held for review).
 */
const TicketCertificatePanel = ({ ticket, onSubmitted }) => {
  const { isGuardian, isClient } = useAuth();

  const derivedTypeId = useMemo(
    () => certificateTypeFromServiceName(ticket?.type?.name),
    [ticket]
  );

  const [documentTypeId, setDocumentTypeId] = useState(
    derivedTypeId ? String(derivedTypeId) : ""
  );
  const [documentKey, setDocumentKey] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [dateCompleted, setDateCompleted] = useState("");
  const [dateExpiration, setDateExpiration] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Only show on a certificate RENEWAL ticket, for staff/contractors.
  // Non-renewal jobs that merely use a certificate service type are excluded — they do
  // not go through the certificate review flow.
  const isRenewalTicket = !!(
    ticket?.is_certificate_renewal === true ||
    ticket?.is_certificate_renewal === 1 ||
    ticket?.is_certificate_renewal === "1"
  );
  const canSubmit = !isGuardian() && !isClient();
  if (!isRenewalTicket || !derivedTypeId || !canSubmit) return null;

  // Already submitted / completed — show a passive note instead of the form.
  const alreadySubmitted = Number(ticket?.status_id) === 7;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setDocumentKey(null);
      setDocumentName(null);
      return;
    }
    const validation = validateCertificateFile(file);
    if (!validation.isValid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const s3Key = await s3Service.uploadDocument(file, {
        folder: "property-certificates",
        allowedTypes: CERTIFICATE_FILE_TYPES,
        maxSize: CERTIFICATE_MAX_SIZE,
        onProgress: (p) => setUploadProgress(p),
      });
      setDocumentKey(s3Key);
      setDocumentName(file.name);
      toast.success("Certificate uploaded");
    } catch (err) {
      console.error("Certificate upload failed:", err);
      toast.error(err.message || "Failed to upload certificate");
      e.target.value = "";
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleIssueDateChange = (e) => {
    const value = e.target.value;
    setDateCompleted(value);
    // Auto-fill expiry from issue date + type validity when expiry is empty.
    if (value && isValidDateUK(value) && !dateExpiration) {
      const suggested = addMonthsUK(value, validityMonthsForType(documentTypeId));
      if (suggested) setDateExpiration(suggested);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!documentTypeId) return toast.error("Select a certificate type");
    if (!documentKey) return toast.error("Upload the certificate file");
    if (!dateCompleted || !isValidDateUK(dateCompleted))
      return toast.error("Enter a valid issue date (DD/MM/YYYY)");
    if (!dateExpiration || !isValidDateUK(dateExpiration))
      return toast.error("Enter a valid expiry date (DD/MM/YYYY)");

    setSubmitting(true);
    try {
      const clientUuid =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `cert-${ticket.id}-${Date.now()}`;
      const payload = {
        document_type_id: documentTypeId,
        document_key: documentKey,
        // Certificates are named by their type, not the uploaded file name.
        name: CERTIFICATE_TYPES[documentTypeId] || "Certificate",
        date_completed: toISODateSafe(dateCompleted),
        date_expiration: toISODateSafe(dateExpiration),
        notes: notes || null,
        client_uuid: clientUuid,
      };
      const res = await ticketsService.submitTicketCertificate(ticket.id, payload);
      if (res?.success) {
        toast.success("Certificate submitted for internal review");
        if (onSubmitted) onSubmitted();
      } else {
        throw new Error(res?.message || "Submission failed");
      }
    } catch (err) {
      console.error("Certificate submit failed:", err);
      toast.error(err.response?.data?.message || err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>
        <h3>Upload certificate &amp; mark complete</h3>
      </div>
      <div className={styles.panelBody}>
        {alreadySubmitted ? (
          <p className={styles.submittedNote}>
            A certificate has been submitted and is pending internal review.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label className={styles.label}>Certificate type</label>
              <select
                className={styles.control}
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
              >
                <option value="">Please select</option>
                {Object.entries(CERTIFICATE_TYPES).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Certificate file</label>
              <input
                type="file"
                className={styles.control}
                accept={CERTIFICATE_ACCEPT}
                onChange={handleFileChange}
                disabled={uploading}
              />
              <div className={styles.hint}>Allowed format: PDF only · max 10 MB</div>
              {uploading && (
                <div className={styles.progress}>Uploading… {Math.round(uploadProgress)}%</div>
              )}
              {documentName && !uploading && (
                <div className={styles.uploaded}>✓ {documentName}</div>
              )}
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Issue date</label>
                <DateInput
                  name="date_completed"
                  value={dateCompleted}
                  onChange={handleIssueDateChange}
                  placeholder="DD/MM/YYYY"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Expiry date</label>
                <DateInput
                  name="date_expiration"
                  value={dateExpiration}
                  onChange={(e) => setDateExpiration(e.target.value)}
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Notes (optional)</label>
              <textarea
                className={styles.control}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={submitting || uploading}
            >
              {submitting ? "Submitting…" : "Submit for review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default TicketCertificatePanel;
